import { Terminal } from "@stripe/terminal-js";
import { useUnmount } from "ahooks";
import { useEffect } from "react";
import { PaymentMethod, Purchase } from "types";
import shallow from "zustand/shallow";
import { usePurchaseResponseStore } from "../components/PurchaseCreate/store/usePurchaseResponseStore";
import { buildCartDisplayInfo } from "../helpers/stripeTerminalDisplayHelper";
import { useCreatePurchaseStore } from "../stores/createPurchaseStore";

/**
 * Custom hook to update Stripe terminal display whenever purchase data changes.
 * This hook monitors the purchase response store and updates the terminal display
 * with the latest cart information whenever the purchase is updated.
 * It also clears the display when payment method is changed to non-credit card.
 */
export const useStripeTerminalDisplay = (readerTerminal: Terminal | null) => {
    const { isEMV, paymentMethod } = useCreatePurchaseStore(
        (state) => ({
            isEMV: state.isEMV,
            paymentMethod: state.paymentLineItem.payment.method,
        }),
        shallow,
    );

    const { items, currency, amount } = usePurchaseResponseStore(
        (state) => ({
            items: state.purchase?.items,
            currency: state.purchase?.currency,
            amount: state.purchase?.amount,
        }),
        shallow,
    );

    useEffect(() => {
        if (!readerTerminal) {
            return;
        }

        const updateTerminalDisplay = async () => {
            try {
                // Clear display if EMV is disabled or payment method is not credit card
                if (!isEMV || paymentMethod !== PaymentMethod.CREDIT_CARD) {
                    await readerTerminal.clearReaderDisplay();
                    console.log(
                        "%cEMV: Cleared reader display (EMV disabled or non-credit card payment)",
                        "color: orange",
                    );
                    return;
                }

                // Clear display if purchase has no items (cancelled or reset)
                if (!items?.length) {
                    await readerTerminal.clearReaderDisplay();
                    console.log("%cEMV: Cleared reader display (no items in purchase)", "color: orange");
                    return;
                }

                const displayInfo = buildCartDisplayInfo({ items, currency, amount } as Purchase);
                await readerTerminal.setReaderDisplay(displayInfo);

                console.log("%cEMV: Updated reader display with cart details", "color: green", displayInfo);
            } catch (error) {
                // Log warning but don't block the flow if display update fails
                console.warn("EMV: Failed to update reader display", error);
            }
        };

        updateTerminalDisplay();
    }, [readerTerminal, isEMV, paymentMethod, items, currency, amount]);

    useUnmount(() => {
        if (readerTerminal) {
            readerTerminal
                .clearReaderDisplay()
                .then(() => {
                    console.log("%cEMV: Cleared reader display on unmount (navigated away)", "color: orange");
                })
                .catch((error) => {
                    console.warn("EMV: Failed to clear reader display on unmount", error);
                });
        }
    });
};
